import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useMemo,
  ReactNode,
} from "react";

export type ThemeColor =
  | "orange"
  | "blue"
  | "cyan"
  | "green"
  | "purple"
  | "rose";

export const THEME_COLORS: Record<
  ThemeColor,
  { name: string; lightHsl: string; darkHsl: string }
> = {
  orange: {
    name: "Orange",
    lightHsl: "hsl(18 85% 55%)",
    darkHsl: "hsl(26 90% 62%)",
  },
  blue: {
    name: "Blue",
    lightHsl: "hsl(220 80% 55%)",
    darkHsl: "hsl(220 80% 60%)",
  },
  cyan: {
    name: "Cyan",
    lightHsl: "hsl(190 80% 45%)",
    darkHsl: "hsl(190 70% 50%)",
  },
  green: {
    name: "Green",
    lightHsl: "hsl(150 60% 40%)",
    darkHsl: "hsl(150 50% 45%)",
  },
  purple: {
    name: "Purple",
    lightHsl: "hsl(270 70% 55%)",
    darkHsl: "hsl(270 60% 60%)",
  },
  rose: {
    name: "Rose",
    lightHsl: "hsl(350 70% 55%)",
    darkHsl: "hsl(350 65% 60%)",
  },
};

export const THEME_COLOR_LIST = Object.entries(THEME_COLORS).map(
  ([key, value]) => ({
    value: key as ThemeColor,
    name: value.name,
    color: value.lightHsl,
  })
);

const STORAGE_KEY = "theme-color";
const DEFAULT_COLOR: ThemeColor = "orange";

// Module-level state to track current color for standalone functions
let currentThemeColor: ThemeColor = DEFAULT_COLOR;

// Event mechanism to sync standalone functions with React Context
type ThemeColorChangeListener = (color: ThemeColor) => void;
const listeners = new Set<ThemeColorChangeListener>();

function notifyThemeColorChange(color: ThemeColor) {
  listeners.forEach((listener) => listener(color));
}

function subscribeToThemeColorChange(listener: ThemeColorChangeListener) {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/**
 * Updates the CSS variables on document.documentElement based on the selected color
 * and the current theme mode (light/dark) detected via body class.
 */
function updateThemeColorVariables(color: ThemeColor) {
  if (typeof document === "undefined") return;

  const isDark = document.body.classList.contains("dark");
  const theme = THEME_COLORS[color];
  // Fallback to default if color is invalid
  const safeTheme = theme || THEME_COLORS[DEFAULT_COLOR];
  const hsl = isDark ? safeTheme.darkHsl : safeTheme.lightHsl;

  const root = document.documentElement;
  root.style.setProperty("--primary", hsl);
  // Always use white foreground for these vibrant primary colors
  root.style.setProperty("--primary-foreground", "hsl(0 0% 100%)");
  root.style.setProperty("--chart-color-1", hsl);

  // Create a shadow color with opacity by replacing the closing parenthesis
  // This assumes the HSL string format is "hsl(h s l)"
  const shadowHsl = hsl.replace(")", " / 0.3)");
  root.style.setProperty("--shadow-focus", shadowHsl);
}

// Initialize immediately
if (typeof window !== "undefined") {
  const saved = localStorage.getItem(STORAGE_KEY) as ThemeColor;
  if (saved && THEME_COLORS[saved]) {
    currentThemeColor = saved;
  }
  updateThemeColorVariables(currentThemeColor);

  // Watch for class changes on body to detect dark mode toggle
  // This ensures we update the color variables when theme mode changes
  const observer = new MutationObserver(() => {
    updateThemeColorVariables(currentThemeColor);
  });
  observer.observe(document.body, {
    attributes: true,
    attributeFilter: ["class"],
  });
}

/**
 * Sets the application theme color.
 * Persists to localStorage and updates CSS variables.
 */
export function setThemeColor(color: ThemeColor): void {
  if (!THEME_COLORS[color]) {
    console.warn(`Invalid theme color: ${color}`);
    return;
  }
  currentThemeColor = color;
  localStorage.setItem(STORAGE_KEY, color);
  updateThemeColorVariables(color);
  notifyThemeColorChange(color);
}

/**
 * Returns the current theme color.
 */
export function getCurrentThemeColor(): ThemeColor {
  return currentThemeColor;
}

// -- React Context & Provider --

interface ThemeColorContextValue {
  color: ThemeColor;
  setColor: (color: ThemeColor) => void;
  colors: typeof THEME_COLOR_LIST;
}

const ThemeColorContext = createContext<ThemeColorContextValue | null>(null);

export function ThemeColorProvider({ children }: { children: ReactNode }) {
  const [color, setColorState] = useState<ThemeColor>(currentThemeColor);

  useEffect(() => {
    const unsubscribe = subscribeToThemeColorChange((newColor) => {
      setColorState(newColor);
    });
    return unsubscribe;
  }, []);

  const value = useMemo(
    () => ({
      color,
      setColor: setThemeColor,
      colors: THEME_COLOR_LIST,
    }),
    [color]
  );

  return (
    <ThemeColorContext.Provider value={value}>
      {children}
    </ThemeColorContext.Provider>
  );
}

export function useThemeColor(): ThemeColorContextValue {
  const context = useContext(ThemeColorContext);
  if (!context) {
    throw new Error("useThemeColor must be used within a ThemeColorProvider");
  }
  return context;
}